<script setup>
import { onMounted, computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
/* global axios */
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRoute, useRouter } from 'vue-router';

import ContactsDetailsLayout from 'dashboard/components-next/Contacts/ContactsDetailsLayout.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ContactDetails from 'dashboard/components-next/Contacts/Pages/ContactDetails.vue';
import ContactNotes from 'dashboard/components-next/Contacts/ContactsSidebar/ContactNotes.vue';
import ContactHistory from 'dashboard/components-next/Contacts/ContactsSidebar/ContactHistory.vue';
import ContactMerge from 'dashboard/components-next/Contacts/ContactsSidebar/ContactMerge.vue';
import ContactCustomAttributes from 'dashboard/components-next/Contacts/ContactsSidebar/ContactCustomAttributes.vue';
import CrmDealsAPI from 'dashboard/api/crm/deals';
import CrmContactTimelineAPI from 'dashboard/api/crm/contactTimeline';

const store = useStore();
const route = useRoute();
const router = useRouter();

const contact = useMapGetter('contacts/getContactById');
const uiFlags = useMapGetter('contacts/getUIFlags');

const contactMergeRef = ref(null);
const contactDeals = ref([]);
const isFetchingDeals = ref(false);

// Accordion expanded sections (multiple can be open)
const expandedSections = ref(new Set(['attributes']));

function toggleSection(key) {
  const s = new Set(expandedSections.value);
  if (s.has(key)) {
    s.delete(key);
  } else {
    s.add(key);
    if (key === 'timeline') fetchContactTimeline();
    if (key === 'deals' && !contactDeals.value.length) fetchContactDeals();
    if (key === 'attributions' && !contactAttributions.value.length) fetchContactAttributions();
    if (key === 'journeys' && !contactJourneys.value.length) fetchContactJourneys();
  }
  expandedSections.value = s;
}

function isSectionOpen(key) {
  return expandedSections.value.has(key);
}

// Timeline state
const timelineEvents = ref([]);
const isLoadingTimeline = ref(false);

// Attribution state
const contactAttributions = ref([]);
const isLoadingAttributions = ref(false);
const expandedAttribId = ref(null);
function toggleAttrib(id) {
  expandedAttribId.value = expandedAttribId.value === id ? null : id;
}

// Journey state
const contactJourneys = ref([]);
const isLoadingJourneys = ref(false);
const expandedJourneyId = ref(null);
function toggleJourney(id) {
  expandedJourneyId.value = expandedJourneyId.value === id ? null : id;
}

const TIMELINE_EVENT_TYPES = {
  inactivity_started: { label: 'Inatividade iniciada', icon: 'i-lucide-clock', color: '#f59e0b' },
  inactivity_template_sent: { label: 'Template enviado', icon: 'i-lucide-send', color: '#3b82f6' },
  inactivity_reply_detected: { label: 'Resposta detectada', icon: 'i-lucide-message-circle', color: '#22c55e' },
  inactivity_max_attempts: { label: 'Máximo de tentativas', icon: 'i-lucide-alert-triangle', color: '#ef4444' },
  deal_stage_changed: { label: 'Etapa alterada', icon: 'i-lucide-arrow-right', color: '#8b5cf6' },
  deal_status_changed: { label: 'Status alterado', icon: 'i-lucide-flag', color: '#f59e0b' },
  deal_created: { label: 'Deal criada', icon: 'i-lucide-plus-circle', color: '#06b6d4' },
  meta_lead: { label: 'Lead via Meta Ads', icon: 'i-lucide-megaphone', color: '#1877f2' },
  automation_triggered: { label: 'Automação disparada', icon: 'i-lucide-zap', color: '#ec4899' },
  follow_up_reply_detected: { label: 'Respondeu no Follow-up', icon: 'i-lucide-message-circle', color: '#16a34a' },
};

function getTimelineEventInfo(eventType) {
  return TIMELINE_EVENT_TYPES[eventType] || { label: eventType, icon: 'i-lucide-activity', color: '#64748b' };
}

const isFetchingItem = computed(() => uiFlags.value.isFetchingItem);
const isMergingContact = computed(() => uiFlags.value.isMerging);
const isUpdatingContact = computed(() => uiFlags.value.isUpdating);

const selectedContact = computed(() => contact.value(route.params.contactId));

const showSpinner = computed(
  () => isFetchingItem.value || isMergingContact.value
);

const { t } = useI18n();

const SIDEBAR_SECTIONS = [
  { key: 'attributes', label: 'Atributos', icon: 'i-lucide-sliders-horizontal' },
  { key: 'history', label: 'Conversas', icon: 'i-lucide-message-square' },
  { key: 'notes', label: 'Notas', icon: 'i-lucide-sticky-note' },
  { key: 'deals', label: 'Oportunidades', icon: 'i-lucide-briefcase' },
  { key: 'journeys', label: 'Follow-ups', icon: 'i-lucide-route' },
  { key: 'attributions', label: 'Atribuições', icon: 'i-lucide-radar' },
  { key: 'timeline', label: 'Timeline', icon: 'i-lucide-activity' },
  { key: 'merge', label: 'Mesclar', icon: 'i-lucide-merge' },
];

const goToContactsList = () => {
  if (window.history.state?.back || window.history.length > 1) {
    router.back();
  } else {
    router.push(`/app/accounts/${route.params.accountId}/contacts?page=1`);
  }
};

const fetchActiveContact = async () => {
  if (route.params.contactId) {
    await store.dispatch('contacts/show', { id: route.params.contactId });
    await store.dispatch(
      'contacts/fetchContactableInbox',
      route.params.contactId
    );
  }
};

const fetchContactNotes = () => {
  const { contactId } = route.params;
  if (contactId) store.dispatch('contactNotes/get', { contactId });
};

const fetchContactConversations = () => {
  const { contactId } = route.params;
  if (contactId) store.dispatch('contactConversations/get', contactId);
};

const fetchAttributes = () => {
  store.dispatch('attributes/get');
};

const fetchContactDeals = async () => {
  const { contactId } = route.params;
  if (!contactId) return;
  isFetchingDeals.value = true;
  try {
    const response = await CrmDealsAPI.getDealsByContact(contactId);
    const deals = response.data?.data || response.data || [];
    contactDeals.value = Array.isArray(deals) ? deals.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)) : [];
  } catch {
    contactDeals.value = [];
  } finally {
    isFetchingDeals.value = false;
  }
};

const fetchContactTimeline = async () => {
  const { contactId } = route.params;
  if (!contactId) return;
  isLoadingTimeline.value = true;
  try {
    const { data } = await CrmContactTimelineAPI.getTimeline(contactId, 1, 50);
    timelineEvents.value = data.data || [];
  } catch {
    timelineEvents.value = [];
  }
  isLoadingTimeline.value = false;
};

const navigateToDeal = (deal) => {
  router.push({
    name: 'crm_deals_index',
    params: { accountId: route.params.accountId },
    query: { deal_id: deal.id },
  });
};

const fetchContactAttributions = async () => {
  const { contactId, accountId } = route.params;
  if (!contactId) return;
  isLoadingAttributions.value = true;
  try {
    const { data } = await axios.get(`/api/v1/accounts/${accountId}/contacts/${contactId}/attributions`);
    contactAttributions.value = data.data || [];
  } catch {
    contactAttributions.value = [];
  }
  isLoadingAttributions.value = false;
};

const JOURNEY_STATUS = {
  active: { label: 'Ativa', bg: '#eff6ff', color: '#3b82f6' },
  replied: { label: 'Respondeu', bg: '#f0fdf4', color: '#16a34a' },
  completed: { label: 'Encerrada', bg: '#fefce8', color: '#ca8a04' },
  cancelled: { label: 'Cancelada', bg: '#fef2f2', color: '#dc2626' },
};
function journeyStatus(s) {
  return JOURNEY_STATUS[s] || { label: s, bg: '#f8fafc', color: '#64748b' };
}

const LOG_ACTION_CONFIG = {
  enrolled: { label: 'Inscrito', icon: 'i-lucide-user-plus', color: '#6366f1' },
  step_sent: { label: 'Etapa executada', icon: 'i-lucide-send', color: '#3b82f6' },
  reply_detected: { label: 'Lead respondeu', icon: 'i-lucide-message-circle', color: '#16a34a' },
  exhausted: { label: 'Jornada encerrada', icon: 'i-lucide-check-circle', color: '#ca8a04' },
  cancelled: { label: 'Cancelada', icon: 'i-lucide-x-circle', color: '#dc2626' },
  error: { label: 'Erro', icon: 'i-lucide-alert-triangle', color: '#ef4444' },
};
function logConfig(action) {
  return LOG_ACTION_CONFIG[action] || { label: action, icon: 'i-lucide-activity', color: '#64748b' };
}

const fetchContactJourneys = async () => {
  const { contactId } = route.params;
  if (!contactId) return;
  isLoadingJourneys.value = true;
  try {
    const { data } = await CrmContactTimelineAPI.getJourneys(contactId);
    contactJourneys.value = data || [];
  } catch {
    contactJourneys.value = [];
  }
  isLoadingJourneys.value = false;
};

const dealStatusLabel = (status) => {
  const map = { open: 'Aberto', won: 'Ganho', lost: 'Perdido' };
  return map[status] || status;
};

const dealStatusColor = (status) => {
  const map = {
    open: { bg: '#eff6ff', color: '#3b82f6' },
    won: { bg: '#f0fdf4', color: '#16a34a' },
    lost: { bg: '#fef2f2', color: '#dc2626' },
  };
  return map[status] || { bg: '#f8fafc', color: '#64748b' };
};

const formatAmount = (val) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0);

const toggleContactBlock = async isBlocked => {
  const ALERT_MESSAGES = {
    success: {
      block: t('CONTACTS_LAYOUT.HEADER.ACTIONS.BLOCK_SUCCESS_MESSAGE'),
      unblock: t('CONTACTS_LAYOUT.HEADER.ACTIONS.UNBLOCK_SUCCESS_MESSAGE'),
    },
    error: {
      block: t('CONTACTS_LAYOUT.HEADER.ACTIONS.BLOCK_ERROR_MESSAGE'),
      unblock: t('CONTACTS_LAYOUT.HEADER.ACTIONS.UNBLOCK_ERROR_MESSAGE'),
    },
  };

  try {
    await store.dispatch(`contacts/update`, {
      ...selectedContact.value,
      blocked: !isBlocked,
    });
    useAlert(
      isBlocked ? ALERT_MESSAGES.success.unblock : ALERT_MESSAGES.success.block
    );
  } catch (error) {
    useAlert(
      isBlocked ? ALERT_MESSAGES.error.unblock : ALERT_MESSAGES.error.block
    );
  }
};

onMounted(() => {
  fetchActiveContact();
  fetchContactNotes();
  fetchContactConversations();
  fetchAttributes();
  fetchContactDeals();
});
</script>

<template>
  <div class="flex flex-col justify-between flex-1 h-full m-0 overflow-auto bg-n-surface-1">
    <ContactsDetailsLayout :button-label="$t('CONTACTS_LAYOUT.HEADER.SEND_MESSAGE')" :selected-contact="selectedContact"
      is-detail-view :show-pagination-footer="false" :is-updating="isUpdatingContact"
      @go-to-contacts-list="goToContactsList" @toggle-block="toggleContactBlock">
      <div v-if="showSpinner" class="flex items-center justify-center py-10 text-n-slate-11">
        <Spinner />
      </div>
      <ContactDetails v-else-if="selectedContact" :selected-contact="selectedContact"
        @go-to-contacts-list="goToContactsList" />
      <template #sidebar>
        <div v-if="isFetchingItem" class="flex items-center justify-center py-10 text-n-slate-11">
          <Spinner />
        </div>
        <div v-else class="flex flex-col gap-1 px-4 py-3">

          <!-- Each accordion section -->
          <div v-for="section in SIDEBAR_SECTIONS" :key="section.key"
            class="rounded-xl border border-n-weak overflow-hidden transition-all">
            <!-- Header (always visible) -->
            <button
              class="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left transition-all cursor-pointer"
              :class="isSectionOpen(section.key)
                ? 'bg-n-alpha-2 dark:bg-n-solid-2'
                : 'hover:bg-n-alpha-1 dark:hover:bg-n-solid-2'"
              @click="toggleSection(section.key)">
              <span :class="section.icon" class="size-4 flex-shrink-0 text-n-slate-10" />
              <span class="flex-1 text-[13px] font-semibold text-n-slate-12">{{ section.label }}</span>
              <span class="i-lucide-chevron-down size-3.5 text-n-slate-8 transition-transform duration-200"
                :class="{ 'rotate-180': isSectionOpen(section.key) }" />
            </button>

            <!-- Collapsible content -->
            <div v-if="isSectionOpen(section.key)" class="border-t border-n-weak">

              <!-- Attributes -->
              <div v-if="section.key === 'attributes'">
                <ContactCustomAttributes :selected-contact="selectedContact" />
              </div>

              <!-- History / Conversations -->
              <div v-if="section.key === 'history'">
                <ContactHistory />
              </div>

              <!-- Notes -->
              <div v-if="section.key === 'notes'">
                <ContactNotes />
              </div>

              <!-- Deals -->
              <div v-if="section.key === 'deals'" class="p-3">
                <div v-if="isFetchingDeals" class="flex items-center justify-center py-6 text-n-slate-11">
                  <Spinner />
                </div>
                <div v-else-if="contactDeals.length === 0" class="text-center py-6">
                  <span class="i-lucide-briefcase size-6 text-n-slate-8 mx-auto block mb-1.5" />
                  <p class="text-xs text-n-slate-10">Nenhuma oportunidade.</p>
                </div>
                <div v-else class="flex flex-col gap-1.5">
                  <div v-for="deal in contactDeals" :key="deal.id"
                    class="flex items-center gap-2.5 p-2.5 rounded-lg border border-n-weak bg-white dark:bg-n-solid-3 cursor-pointer transition-all hover:shadow-sm hover:border-woot-200 dark:hover:border-woot-700 group"
                    @click="navigateToDeal(deal)">
                    <div class="size-7 rounded-md flex items-center justify-center flex-shrink-0"
                      :style="{ background: dealStatusColor(deal.status).bg }">
                      <span v-if="deal.status === 'open'" class="i-lucide-circle-dot size-3.5"
                        :style="{ color: dealStatusColor(deal.status).color }" />
                      <span v-else-if="deal.status === 'won'" class="i-lucide-check-circle-2 size-3.5"
                        :style="{ color: dealStatusColor(deal.status).color }" />
                      <span v-else class="i-lucide-x-circle size-3.5"
                        :style="{ color: dealStatusColor(deal.status).color }" />
                    </div>
                    <div class="flex-1 min-w-0">
                      <span class="text-xs font-semibold text-n-slate-12 truncate block group-hover:text-woot-500 transition-colors">{{ deal.name }}</span>
                      <div class="flex items-center gap-1.5 text-[10px] text-n-slate-10">
                        <span v-if="deal.pipeline_name" class="truncate">{{ deal.pipeline_name }}</span>
                        <span v-if="deal.pipeline_name && deal.stage_name">·</span>
                        <span v-if="deal.stage_name" class="truncate">{{ deal.stage_name }}</span>
                      </div>
                    </div>
                    <div class="text-right flex-shrink-0">
                      <div class="text-xs font-bold text-n-slate-12">{{ formatAmount(deal.amount) }}</div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Timeline -->
              <div v-if="section.key === 'timeline'" class="p-3">
                <div v-if="isLoadingTimeline" class="flex items-center justify-center py-6 gap-2">
                  <span class="animate-spin i-lucide-loader-2 size-4 text-woot-500" />
                  <span class="text-xs text-n-slate-9">Carregando...</span>
                </div>
                <div v-else-if="!timelineEvents.length" class="text-center py-6">
                  <span class="i-lucide-inbox size-6 text-n-slate-8 mx-auto block mb-1.5" />
                  <p class="text-xs text-n-slate-10">Nenhum evento registrado.</p>
                </div>
                <div v-else class="flex flex-col gap-0">
                  <div v-for="evt in timelineEvents" :key="evt.id"
                    class="flex items-start gap-2.5 py-2.5 border-b border-n-weak last:border-0">
                    <div class="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center"
                      :style="{ background: getTimelineEventInfo(evt.event_type).color + '18' }">
                      <span :class="getTimelineEventInfo(evt.event_type).icon" class="size-3.5"
                        :style="{ color: getTimelineEventInfo(evt.event_type).color }" />
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-1.5 mb-0.5 flex-wrap">
                        <span class="text-[11px] font-semibold text-n-slate-12">{{ getTimelineEventInfo(evt.event_type).label }}</span>
                        <span v-if="evt.metadata?.deal_name" class="text-[9px] px-1 py-0.5 rounded-full font-medium bg-n-alpha-2 text-n-slate-10">
                          {{ evt.metadata.deal_name }}
                        </span>
                      </div>
                      <p class="text-[10px] mb-0.5 text-n-slate-10 leading-snug">{{ evt.title }}</p>
                      <div v-if="evt.metadata" class="flex flex-wrap gap-1 mt-0.5">
                        <span v-if="evt.metadata?.source === 'meta_lead_ads'" class="text-[9px] px-1 py-0.5 rounded font-semibold"
                          style="background: #dbeafe; color: #1d4ed8;">
                          Meta Lead Ads
                        </span>
                        <span v-if="evt.metadata?.form_name" class="text-[9px] px-1 py-0.5 rounded"
                          style="background: #e0f2fe; color: #0369a1;">
                          📋 {{ evt.metadata.form_name }}
                        </span>
                        <span v-if="evt.metadata?.page_name" class="text-[9px] px-1 py-0.5 rounded"
                          style="background: #f0fdf4; color: #166534;">
                          📄 {{ evt.metadata.page_name }}
                        </span>
                        <span v-if="evt.metadata.from_stage && evt.metadata.to_stage" class="text-[9px] px-1 py-0.5 rounded"
                          style="background: #f3e8ff; color: #7c3aed;">
                          {{ evt.metadata.from_stage }} → {{ evt.metadata.to_stage }}
                        </span>
                        <span v-if="evt.metadata.from_status && evt.metadata.to_status" class="text-[9px] px-1 py-0.5 rounded"
                          style="background: #fef9c3; color: #a16207;">
                          {{ evt.metadata.from_status }} → {{ evt.metadata.to_status }}
                        </span>
                      </div>
                      <span class="text-[9px] mt-1 block text-n-slate-8">{{ new Date(evt.created_at).toLocaleString('pt-BR') }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Jornadas -->
              <div v-if="section.key === 'journeys'" class="p-3">
                <div v-if="isLoadingJourneys" class="flex items-center justify-center py-6 gap-2">
                  <span class="animate-spin i-lucide-loader-2 size-4 text-woot-500" />
                  <span class="text-xs text-n-slate-9">Carregando...</span>
                </div>
                <div v-else-if="!contactJourneys.length" class="text-center py-6">
                  <span class="i-lucide-route size-6 text-n-slate-8 mx-auto block mb-1.5" />
                  <p class="text-xs text-n-slate-10">Nenhum follow-up registrado.</p>
                </div>
                <div v-else class="flex flex-col gap-1.5">
                  <div v-for="j in contactJourneys" :key="j.enrollment_id"
                    class="rounded-lg border border-n-weak overflow-hidden bg-white dark:bg-n-solid-3">
                    <!-- Journey Header -->
                    <button class="w-full flex items-center gap-2 px-2.5 py-2 text-left hover:bg-n-alpha-1 transition cursor-pointer"
                      @click="toggleJourney(j.enrollment_id)">
                      <div class="size-7 rounded-md flex items-center justify-center flex-shrink-0"
                        :style="{ background: journeyStatus(j.status).bg }">
                        <span class="i-lucide-route size-3.5" :style="{ color: journeyStatus(j.status).color }" />
                      </div>
                      <div class="flex-1 min-w-0">
                        <span class="text-[11px] font-semibold text-n-slate-12 truncate block">{{ j.cadence_name }}</span>
                        <div class="flex items-center gap-1.5 text-[9px] text-n-slate-9">
                          <span v-if="j.deal_name" class="truncate">{{ j.deal_name }}</span>
                          <span>·</span>
                          <span>{{ j.current_step }}/{{ j.total_steps }} etapas</span>
                        </div>
                      </div>
                      <span class="px-1.5 py-0.5 rounded-full text-[9px] font-semibold flex-shrink-0"
                        :style="{ background: journeyStatus(j.status).bg, color: journeyStatus(j.status).color }">
                        {{ journeyStatus(j.status).label }}
                      </span>
                      <span class="i-lucide-chevron-down size-3 text-n-slate-8 flex-shrink-0 transition-transform duration-200"
                        :class="{ 'rotate-180': expandedJourneyId === j.enrollment_id }" />
                    </button>

                    <!-- Journey Logs (Expanded) -->
                    <div v-if="expandedJourneyId === j.enrollment_id" class="border-t border-n-weak px-3 py-2">
                      <div v-if="j.conversion_step != null"
                        class="mb-2 px-2 py-1.5 rounded-lg flex items-center gap-2"
                        style="background: #f0fdf4; border: 1px solid #bbf7d0;">
                        <span class="i-lucide-sparkles size-3.5" style="color: #16a34a;" />
                        <span class="text-[10px] font-semibold" style="color: #15803d;">
                          Converteu na Etapa {{ j.conversion_step + 1 }}
                          <span v-if="j.conversion_at" class="font-normal text-[9px] ml-1" style="color: #4ade80;">
                            · {{ new Date(j.conversion_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) }}
                          </span>
                        </span>
                      </div>
                      <!-- Log Timeline -->
                      <div class="relative ml-1">
                        <div class="absolute left-[7px] top-2 bottom-2 w-px bg-n-weak" />
                        <div v-for="log in j.logs" :key="log.id" class="relative flex gap-2.5 pb-2 last:pb-0">
                          <div class="relative z-10 flex-shrink-0 mt-1.5">
                            <div class="size-4 rounded-full flex items-center justify-center"
                              :style="{ background: logConfig(log.action).color + '18' }">
                              <span :class="logConfig(log.action).icon" class="size-2.5"
                                :style="{ color: logConfig(log.action).color }" />
                            </div>
                          </div>
                          <div class="flex-1 min-w-0 pt-0.5"
                            :class="log.action === 'reply_detected' ? 'bg-green-50/50 dark:bg-green-900/10 -mx-1 px-1 rounded' : ''">
                            <div class="flex items-center gap-1.5">
                              <span class="text-[10px] font-semibold text-n-slate-12">{{ logConfig(log.action).label }}</span>
                              <span v-if="log.step_number != null"
                                class="text-[9px] px-1 py-0.5 rounded font-medium bg-n-alpha-2 text-n-slate-10">
                                Etapa {{ log.step_number + 1 }}
                              </span>
                            </div>
                            <p v-if="log.details" class="text-[9px] text-n-slate-9 mt-0.5 leading-snug">{{ log.details }}</p>
                            <span class="text-[8px] text-n-slate-8 mt-0.5 block">
                              {{ new Date(log.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Attributions -->
              <div v-if="section.key === 'attributions'" class="p-3">
                <div v-if="isLoadingAttributions" class="flex items-center justify-center py-6 gap-2">
                  <span class="animate-spin i-lucide-loader-2 size-4 text-woot-500" />
                  <span class="text-xs text-n-slate-9">Carregando...</span>
                </div>
                <div v-else-if="!contactAttributions.length" class="text-center py-6">
                  <span class="i-lucide-route size-6 text-n-slate-8 mx-auto block mb-1.5" />
                  <p class="text-xs text-n-slate-10">Nenhum registro de atribuição.</p>
                </div>
                <div v-else class="relative ml-1">
                  <div class="absolute left-[7px] top-2 bottom-2 w-px bg-n-weak" />
                  <div v-for="attr in contactAttributions" :key="attr.id" class="relative flex gap-3 pb-2 last:pb-0">
                    <div class="relative z-10 flex-shrink-0 mt-2">
                      <div class="size-4 rounded-full border-2 flex items-center justify-center"
                        :class="attr.source === 'form_submission'
                          ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/30'
                          : attr.source === 'meta_lead_ads'
                            ? 'border-indigo-400 bg-indigo-50 dark:bg-indigo-900/30'
                            : 'border-emerald-400 bg-emerald-50 dark:bg-emerald-900/30'">
                        <span class="size-2" :class="attr.source === 'form_submission'
                          ? 'i-lucide-file-text'
                          : attr.source === 'meta_lead_ads'
                            ? 'i-lucide-megaphone'
                            : 'i-lucide-radar'" />
                      </div>
                    </div>
                    <div class="flex-1 min-w-0 rounded-lg border border-n-weak overflow-hidden bg-n-solid-1">
                      <!-- Header / toggle button -->
                      <button class="w-full flex items-center gap-2 px-2.5 py-2 text-left hover:bg-n-alpha-1 transition"
                        @click="toggleAttrib(attr.id)">
                        <div class="flex-1 min-w-0">
                          <span class="text-[11px] font-semibold text-n-slate-12 truncate block">
                            {{ attr.source_name || (attr.source === 'form_submission' ? 'Formulário' : attr.source === 'meta_lead_ads' ? 'Meta Lead Ads' : 'Tracking') }}
                          </span>
                          <span class="text-[9px] text-n-slate-8">{{ new Date(attr.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) }}</span>
                        </div>
                        <span class="px-1 py-0.5 rounded text-[9px] font-medium flex-shrink-0"
                          :class="attr.source === 'form_submission'
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                            : attr.source === 'meta_lead_ads'
                              ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400'
                              : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'">
                          {{ attr.source === 'form_submission' ? 'Form' : attr.source === 'meta_lead_ads' ? 'Meta' : 'Track' }}
                        </span>
                        <span class="i-lucide-chevron-down size-3 text-n-slate-8 flex-shrink-0 transition-transform duration-200"
                          :class="{ 'rotate-180': expandedAttribId === attr.id }" />
                      </button>
                      <!-- Expandable details -->
                      <div v-if="expandedAttribId === attr.id" class="border-t border-n-weak px-2.5 pb-2.5 pt-1.5">
                        <div v-if="attr.gclid || attr.fbclid" class="flex gap-1 mb-1.5">
                          <span v-if="attr.gclid" class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 text-[9px] font-medium">
                            <span class="i-lucide-mouse-pointer-click size-2" /> GCLID
                          </span>
                          <span v-if="attr.fbclid" class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 text-[9px] font-medium">
                            <span class="i-lucide-mouse-pointer-click size-2" /> FBCLID
                          </span>
                        </div>
                        <div v-if="attr.params && Object.keys(attr.params).length > 0">
                          <div v-for="(v, k) in attr.params" :key="k" class="flex gap-2 py-1 border-b border-n-weak/50 last:border-0">
                            <span class="text-[10px] font-medium text-n-slate-10 min-w-[70px] capitalize">{{ k }}</span>
                            <span class="text-[10px] text-n-slate-12 break-all">{{ v || '—' }}</span>
                          </div>
                        </div>
                        <div v-else class="text-[10px] text-n-slate-8 italic">Sem parâmetros</div>
                        <div v-if="attr.referrer_url" class="mt-1.5 pt-1.5 border-t border-n-weak/50">
                          <span class="text-[10px] font-medium text-n-slate-10">Origem</span>
                          <p class="text-[10px] text-n-slate-12 break-all mt-0.5">{{ attr.referrer_url }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Merge -->
              <div v-if="section.key === 'merge'">
                <ContactMerge ref="contactMergeRef" :selected-contact="selectedContact"
                  @go-to-contacts-list="goToContactsList" @reset-tab="expandedSections.delete('merge')" />
              </div>

            </div>
          </div>

        </div>
      </template>
    </ContactsDetailsLayout>
  </div>
</template>
